import {
  Button,
  PhoneMockup,
  PhoneMockupBodyLayout,
  PhoneMockupLayout,
  WidgetHeader,
  WidgetHeaderContent,
  WidgetStatus,
} from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';
import { useEffect, useState } from 'react';
import { isOfframpBill } from '../../utils';

import {
  TopBar,
  TransactionDropdown,
  TransactionDropdownProps,
} from '../../components';
import links from '../../constants/links';
import { useWidgetContext } from '../../context/WidgetContext';

import { useRegisterHotjarPage } from '../../hooks';
import BlockAnimation, { useBlockAnimation } from './components/BlockAnimation';
import styles from './PaymentPending.module.css';
import { useSubscription } from '@apollo/client';
import { SUBSCRIBE_TO_BLOCK_DETECTED } from '../../graphql/subscriptions';
import { getBlockchainData, saveBlockchainData } from './utils';

export interface PaymentPendingProps {
  merchantName: string;
  expiresAt: number;
  transactions: TransactionDropdownProps['transactions'];
  blockchain: string;
  noOfConfirmationBlocks: number | null;
  billType: string;
}

const PaymentPending = ({
  expiresAt,
  blockchain,
  merchantName,
  transactions,
  noOfConfirmationBlocks: totalBlocks,
  billType,
}: PaymentPendingProps) => {
  const { configureHeader } = useWidgetContext();
  useRegisterHotjarPage('payment-pending');

  useEffect(() => {
    configureHeader({
      id: getIdFromUrl(),
      expiresAt: expiresAt,
      merchantName,
    });
  }, [expiresAt]);

  const { data: blockDetectedData } = useSubscription(
    SUBSCRIBE_TO_BLOCK_DETECTED,
    {
      variables: { chain: blockchain },
      skip: !blockchain,
    }
  );

  const { mintBlock, ...rest } = useBlockAnimation();

  const [previousBlockNumber, setPreviousBlockNumber] = useState<number | null>(
    () => getBlockchainData(blockchain)
  );

  const initialBlock = transactions.find(
    (t) => t?.status === 'detected'
  )?.blockNumber;

  const currentBlock: null | number =
    blockDetectedData?.blockDetected.blockNumber || previousBlockNumber;

  const blocksConfirmed = Math.min(
    currentBlock && initialBlock
      ? Math.max(0, currentBlock - Number(initialBlock))
      : 0,
    totalBlocks ?? 0
  );

  const [hasFinished, setHasFinished] = useState(false);

  useEffect(() => {
    const newBlockNumber = blockDetectedData?.blockDetected.blockNumber;

    if (!newBlockNumber || !totalBlocks || hasFinished) {
      return;
    }

    if (previousBlockNumber === null) {
      setPreviousBlockNumber(newBlockNumber);
      saveBlockchainData(blockchain, newBlockNumber);
      return;
    }

    if (newBlockNumber === previousBlockNumber) {
      return;
    }

    setPreviousBlockNumber(newBlockNumber);
    saveBlockchainData(blockchain, newBlockNumber);

    if (blocksConfirmed <= totalBlocks) {
      mintBlock(totalBlocks - blocksConfirmed);
    }

    if (blocksConfirmed === totalBlocks) {
      setHasFinished(true);
    }
  }, [
    blockDetectedData?.blockDetected.blockNumber,
    previousBlockNumber,
    totalBlocks,
    blocksConfirmed,
    hasFinished,
    mintBlock,
    blockchain,
  ]);

  const isOffRamp = isOfframpBill(billType);

  return (
    <PhoneMockupLayout>
      <PhoneMockup
        header={
          <WidgetHeader color="default">
            <WidgetHeaderContent
              title={
                initialBlock
                  ? `Processing ${isOffRamp ? 'off ramp' : 'payment'}`
                  : isOffRamp
                  ? 'Transaction pending'
                  : 'Payment pending'
              }
              loading
            />
          </WidgetHeader>
        }
      >
        <PhoneMockupBodyLayout
          header={
            <TopBar
              rightSlot={<TransactionDropdown transactions={transactions} />}
            />
          }
          bodyCentered
          body={
            initialBlock ? (
              <>
                <div className={styles['block-animation']}>
                  <BlockAnimation {...rest} />
                </div>
                <WidgetStatus
                  title={`${blocksConfirmed}/${totalBlocks} blocks confirmed`}
                  description={
                    <>
                      We're confirming the necessary transaction blocks.
                      <br /> This can take a few moments.
                    </>
                  }
                />
              </>
            ) : (
              <WidgetStatus
                emoji="🔎"
                title={isOffRamp ? 'Transaction pending' : 'Payment pending'}
                description={
                  isOffRamp
                    ? 'We found your transaction in the blockchain and we’re just waiting for confirmation. Almost there!'
                    : 'We found your payment in the blockchain and we’re just waiting for confirmation. Almost there!'
                }
              />
            )
          }
          footer={
            <Button
              as="a"
              size="large"
              href={links.support.PAYMENT_CONFIRMATION}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
            >
              Learn more
            </Button>
          }
        />
      </PhoneMockup>
    </PhoneMockupLayout>
  );
};

export default PaymentPending;
